// ============================================
// TEAM CONNECTIONS API
// ============================================
export const teamConnectionsAPI = {
  // List all team connections for user
  getAll: async () => {
    return authenticatedFetch(`${API_BASE_URL}/user/team-connections`);
  },
  // Send team connection request
  create: async (connection) => {
    return authenticatedFetch(`${API_BASE_URL}/user/team-connections`, {
      method: 'POST',
      body: JSON.stringify(connection)
    });
  },
  // Accept/decline team connection
  updateStatus: async (connectionId, status) => {
    return authenticatedFetch(`${API_BASE_URL}/user/team-connections/${connectionId}`, {
      method: 'PUT',
      body: JSON.stringify({ status })
    });
  },
  // Remove team connection
  remove: async (connectionId) => {
    return authenticatedFetch(`${API_BASE_URL}/user/team-connections/${connectionId}`, {
      method: 'DELETE'
    });
  },
  // Search designers with team access
  searchTeam: async (params) => {
    const queryParams = new URLSearchParams();
    if (params.query) queryParams.append('query', params.query);
    if (params.city) queryParams.append('city', params.city);
    if (params.limit) queryParams.append('limit', params.limit);
    return authenticatedFetch(`${API_BASE_URL}/user/team-search?${queryParams.toString()}`);
  }
};
// API Service Layer - handles all backend communication
// This replaces localStorage calls with HTTP requests to PostgreSQL backend

// Use relative URL in production (Vercel), absolute URL in development
const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

// Helper function to get auth token from localStorage
const getAuthToken = () => {
  let token = localStorage.getItem('authToken');
  if (!token) return null;

  // Some devices store the token with extra quotes or already prefixed with "Bearer "
  token = token.toString().trim();
  if (token.startsWith('Bearer ')) token = token.slice('Bearer '.length).trim();
  if ((token.startsWith('"') && token.endsWith('"')) || (token.startsWith("'") && token.endsWith("'"))) {
    try {
      token = JSON.parse(token);
    } catch {
      token = token.slice(1, -1);
    }
  }
  return token;
};

// Helper function to make authenticated requests
const authenticatedFetch = async (url, options = {}) => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(url, {
    ...options,
    headers
  });
  
  if (response.status === 401 || response.status === 403) {
    // Token expired or invalid - clear auth and redirect to login
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('userData');
    localStorage.removeItem('userId');
    window.location.href = '/';
    throw new Error('Authentication required');
  }
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  
  return response.json();
};

// ============================================
// PROFILE API
// ============================================

export const profileAPI = {
  // Get user's profile
  get: async () => {
    return authenticatedFetch(`${API_BASE_URL}/user/profile`);
  },
  
  // Update user's profile
  update: async (profileData) => {
    return authenticatedFetch(`${API_BASE_URL}/user/profile`, {
      method: 'PUT',
      body: JSON.stringify(profileData)
    });
  }
};

// ============================================
// CLIENTS API
// ============================================

export const clientsAPI = {
  // Get all homeowners
  getAll: async () => {
    return authenticatedFetch(`${API_BASE_URL}/user/homeowners`);
  },
  
  // Create new client
  create: async (client) => {
    return authenticatedFetch(`${API_BASE_URL}/user/homeowners`, {
      method: 'POST',
      body: JSON.stringify(client)
    });
  },
  
  // Update existing client
  update: async (clientId, updates) => {
    return authenticatedFetch(`${API_BASE_URL}/user/homeowners/${clientId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  },
  
  // Delete client
  delete: async (clientId) => {
    return authenticatedFetch(`${API_BASE_URL}/user/homeowners/${clientId}`, {
      method: 'DELETE'
    });
  }
};

// ============================================
// VISITS/FORMULAS API
// ============================================

export const visitsAPI = {
  // Get all visits
  getAll: async () => {
    return authenticatedFetch(`${API_BASE_URL}/user/visits`);
  },
  
  // Create new visit
  create: async (visit) => {
    return authenticatedFetch(`${API_BASE_URL}/user/visits`, {
      method: 'POST',
      body: JSON.stringify(visit)
    });
  },
  
  // Update existing visit
  update: async (visitId, updates) => {
    return authenticatedFetch(`${API_BASE_URL}/user/visits/${visitId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  },
  
  // Delete visit
  delete: async (visitId) => {
    return authenticatedFetch(`${API_BASE_URL}/user/visits/${visitId}`, {
      method: 'DELETE'
    });
  }
};

// ============================================
// CONNECTIONS API
// ============================================

export const connectionsAPI = {
  // Get all connections
  getAll: async () => {
    return authenticatedFetch(`${API_BASE_URL}/user/connections`);
  },
  
  // Send connection request
  create: async (connection) => {
    return authenticatedFetch(`${API_BASE_URL}/user/connections`, {
      method: 'POST',
      body: JSON.stringify(connection)
    });
  },
  
  // Accept/decline connection
  updateStatus: async (connectionId, status) => {
    return authenticatedFetch(`${API_BASE_URL}/user/connections/${connectionId}`, {
      method: 'PUT',
      body: JSON.stringify({ status })
    });
  },
  
  // Update connection type (referral, team_pending, team_member)
  updateConnectionType: async (connectionId, connectionType) => {
    return authenticatedFetch(`${API_BASE_URL}/user/connections/${connectionId}`, {
      method: 'PUT',
      body: JSON.stringify({ connectionType })
    });
  }
};

// ============================================
// COMPLETE DATA LOAD (replaces loadDB)
// ============================================

export const loadUserData = async () => {
  try {
    const toTimestamp = (value) => value ? new Date(value).getTime() : Date.now();
    const ensureArray = (value) => {
      if (Array.isArray(value)) return value;
      if (value === null || value === undefined || value === '') return [];
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      }
      return [];
    };

    const [profile, homeowners, visits, connections] = await Promise.all([
      profileAPI.get(),
      clientsAPI.getAll(),
      visitsAPI.getAll(),
      connectionsAPI.getAll()
    ]);
    
    return {
      profile: {
        id: profile.id || null,
        displayName: profile.display_name || '',
        fullName: profile.full_name || '',
        salonName: profile.salon_name || '',
        email: profile.email || '',
        phone: profile.phone || '',
        city: profile.city || '',
        country: profile.country || '',
        bio: profile.bio || '',
        photoUrl: profile.profile_photo_url || '',
        license: profile.license_number || '',
        brands: ensureArray(profile.brands),
        skillsOffered: ensureArray(profile.skills_offered),
        servicesExcluded: ensureArray(profile.services_excluded),
        openToClients: profile.open_to_clients || false,
        openToConnections: profile.open_to_connections || false,
        whatILove: profile.what_i_love || '',
        bookingLink: profile.booking_link || '',
        website: profile.website || '',
        instagram: profile.instagram || '',
        streetAddress: profile.street_address || '',
        portfolioPhotos: ensureArray(profile.portfolio_photos),
        isPremium: profile.is_premium || false,
        marketplacePublished: profile.marketplace_published || false,
        hairTypeSpecializations: ensureArray(profile.hair_type_specializations),
        professionalCategory: profile.professional_category || 'landscape_designer',
        emailDigestEnabled: profile.email_digest_enabled !== undefined ? profile.email_digest_enabled : true
      },
      homeowners: homeowners.map(c => ({
        id: c.id,
        name: c.name,
        phone: c.phone || '',
        email: c.email || '',
        texture: c.texture || '',
        allergies: c.allergies || '',
        notes: c.notes || '',
        createdAt: toTimestamp(c.created_at),
        updatedAt: toTimestamp(c.updated_at)
      })),
      visits: visits.map(v => ({
        id: v.id,
        clientId: v.client_id,
        rawNotes: v.raw_notes,
        createdAt: toTimestamp(v.created_at),
        updatedAt: toTimestamp(v.updated_at),
        photos: ensureArray(v.photos)
      })),
      connections: connections.map(c => ({
        ...c,
        requesterId: c.requester_id,
        recipientId: c.recipient_id,
        connectionType: c.connection_type || 'referral',
        connectionName: c.connection_name || '',
        connectionCity: c.connection_city || '',
        createdAt: toTimestamp(c.created_at),
        updatedAt: toTimestamp(c.updated_at)
      }))
    };
  } catch (error) {
    console.error('Failed to load user data:', error);
    throw error;
  }
};

// ============================================
// DATA MIGRATION HELPER
// ============================================

// Migrate localStorage data to PostgreSQL
export const migrateLocalStorageData = async () => {
  const LS_KEY = 'plantaday-v4';
  const localData = localStorage.getItem(LS_KEY);
  
  if (!localData) {
    return { migrated: false, message: 'No local data found' };
  }
  
  try {
    const db = JSON.parse(localData);
    const results = {
      homeowners: 0,
      visits: 0,
      connections: 0,
      errors: []
    };
    
    // Migrate profile
    if (db.profile) {
      try {
        await profileAPI.update({
          display_name: db.profile.displayName,
          salon_name: db.profile.salonName,
          brands: db.profile.brands,
          skills_offered: db.profile.skillsOffered,
          services_excluded: db.profile.servicesExcluded,
          open_to_clients: db.profile.openToClients,
          open_to_connections: db.profile.openToConnections
        });
      } catch (err) {
        results.errors.push(`Profile: ${err.message}`);
      }
    }
    
    // Migrate homeowners
    if (db.homeowners) {
      for (const client of db.homeowners) {
        try {
          await clientsAPI.create(client);
          results.homeowners++;
        } catch (err) {
          results.errors.push(`Client ${client.name}: ${err.message}`);
        }
      }
    }
    
    // Migrate visits
    if (db.visits) {
      for (const visit of db.visits) {
        try {
          await visitsAPI.create({
            id: visit.id,
            client_id: visit.clientId,
            raw_notes: visit.rawNotes
          });
          results.visits++;
        } catch (err) {
          results.errors.push(`Visit ${visit.id}: ${err.message}`);
        }
      }
    }
    
    // Migrate connections
    if (db.connections) {
      for (const conn of db.connections) {
        try {
          await connectionsAPI.create(conn);
          results.connections++;
        } catch (err) {
          results.errors.push(`Connection ${conn.id}: ${err.message}`);
        }
      }
    }
    
    return {
      migrated: true,
      results
    };
  } catch (error) {
    throw new Error(`Migration failed: ${error.message}`);
  }
};

// ============================================
// Team Visits API (for AI search)
// ============================================
const teamVisitsAPI = {
  search: async (keywords) => {
    const keywordString = Array.isArray(keywords) ? keywords.join(',') : keywords;
    const queryParams = keywordString ? `?keywords=${encodeURIComponent(keywordString)}` : '';
    return authenticatedFetch(`${API_BASE_URL}/user/team-visits${queryParams}`);
  }
};

// ============================================
// Designer Search/Discovery API
// ============================================
const designersAPI = {
  search: async (query, city = null) => {
    const params = new URLSearchParams();
    if (query) params.append('query', query);
    if (city) params.append('city', city);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return authenticatedFetch(`${API_BASE_URL}/user/search-designers${queryString}`);
  },
  
  browse: async (page = 1, city = null) => {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('limit', 20);
    if (city) params.append('city', city);
    return authenticatedFetch(`${API_BASE_URL}/user/browse-designers?${params.toString()}`);
  }
};

// ============================================
// MESSAGING API
// ============================================
const messagesAPI = {
  // Get all messages for a conversation with a specific connection
  getConversation: async (connectionId) => {
    return authenticatedFetch(`${API_BASE_URL}/user/messages/${connectionId}`);
  },
  
  // Send a new message
  send: async (connectionId, content) => {
    return authenticatedFetch(`${API_BASE_URL}/user/messages`, {
      method: 'POST',
      body: JSON.stringify({ connectionId, content })
    });
  }
};

// ============================================
// INVITE LINKS API
// ============================================

export const inviteLinksAPI = {
  // Create invite link
  create: async (clientName, clientFormula, inviteeEmail) => {
    return authenticatedFetch(`${API_BASE_URL}/user/invite-links`, {
      method: 'POST',
      body: JSON.stringify({ clientName, clientFormula, inviteeEmail })
    });
  },
  
  // Get invite details (public)
  get: async (code) => {
    const response = await fetch(`${API_BASE_URL}/user/invite-links/${code}`);
    if (!response.ok) {
      throw new Error('Failed to fetch invite');
    }
    return response.json();
  },
  
  // Redeem invite
  redeem: async (code) => {
    return authenticatedFetch(`${API_BASE_URL}/user/invite-links/${code}/redeem`, {
      method: 'POST'
    });
  }
};

// ============================================
// DESIGNER SEARCH API
// ============================================

export const designerSearchAPI = {
  search: async (params) => {
    const queryParams = new URLSearchParams();
    if (params.city) queryParams.append('city', params.city);
    if (params.skill) queryParams.append('skill', params.skill);
    if (params.query) queryParams.append('query', params.query);
    
    return authenticatedFetch(`${API_BASE_URL}/user/search-designers?${queryParams.toString()}`);
  }
};

// ============================================
// PERKS API
// ============================================

export const perksAPI = {
  // Get all perks for current user
  getAll: async () => {
    return authenticatedFetch(`${API_BASE_URL}/user/perks`);
  },
  
  // Create new perk
  create: async (perkData) => {
    return authenticatedFetch(`${API_BASE_URL}/user/perks`, {
      method: 'POST',
      body: JSON.stringify(perkData)
    });
  },
  
  // Update existing perk
  update: async (perkId, perkData) => {
    return authenticatedFetch(`${API_BASE_URL}/user/perks/${perkId}`, {
      method: 'PUT',
      body: JSON.stringify(perkData)
    });
  },
  
  // Delete perk
  delete: async (perkId) => {
    return authenticatedFetch(`${API_BASE_URL}/user/perks/${perkId}`, {
      method: 'DELETE'
    });
  }
};

// ============================================
// MATCH WANTED API
// ============================================
export const matchWantedAPI = {
  // Get Match Wanted posts with filtering and sorting
  getPosts: async (filters = {}) => {
    const queryParams = new URLSearchParams();
    
    if (filters.city) queryParams.append('city', filters.city);
    if (filters.hair_types && filters.hair_types.length > 0) {
      filters.hair_types.forEach(type => queryParams.append('hair_types', type));
    }
    if (filters.style_tags && filters.style_tags.length > 0) {
      filters.style_tags.forEach(tag => queryParams.append('style_tags', tag));
    }
    if (filters.budgets && filters.budgets.length > 0) {
      filters.budgets.forEach(budget => queryParams.append('budgets', budget));
    }
    if (filters.timelines && filters.timelines.length > 0) {
      filters.timelines.forEach(timeline => queryParams.append('timelines', timeline));
    }
    if (filters.sort) queryParams.append('sort', filters.sort);
    
    return authenticatedFetch(`${API_BASE_URL}/match-wanted/posts?${queryParams.toString()}`);
  },
  
  // Get list of cities with active posts
  getAvailableCities: async () => {
    return authenticatedFetch(`${API_BASE_URL}/match-wanted/available-cities`);
  },
  
  // Express interest in a Match Wanted post
  expressInterest: async (matchWantedId, designerMessage) => {
    return authenticatedFetch(`${API_BASE_URL}/match-wanted/interest`, {
      method: 'POST',
      body: JSON.stringify({ 
        match_wanted_id: matchWantedId,
        designer_message: designerMessage
      })
    });
  },

  // Resend the interest email (optionally with an updated message)
  resendInterestEmail: async (matchWantedId, designerMessage) => {
    const payload = {
      match_wanted_id: matchWantedId
    };

    if (designerMessage) {
      payload.designer_message = designerMessage;
    }

    return authenticatedFetch(`${API_BASE_URL}/match-wanted/resend-email`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }
};

// ============================================
// MESSAGING API
// ============================================
export const messagingAPI = {
  // Get all conversations
  getConversations: async () => {
    return authenticatedFetch(`${API_BASE_URL}/messages/conversations`);
  },
  
  // Get full conversation with messages
  getConversation: async (conversationId) => {
    return authenticatedFetch(`${API_BASE_URL}/messages/conversation/${conversationId}`);
  },
  
  // Send a message
  sendMessage: async (conversationId, messageText) => {
    return authenticatedFetch(`${API_BASE_URL}/messages/send`, {
      method: 'POST',
      body: JSON.stringify({ 
        conversation_id: conversationId, 
        message_text: messageText 
      })
    });
  },
  
  // Get unread message count
  getUnreadCount: async () => {
    return authenticatedFetch(`${API_BASE_URL}/messages/unread-count`);
  }
};

// ============================================
// AI SEARCH API (Gemini Integration)
// ============================================
export const aiSearchAPI = {
  // Query AI with context and get natural language response
  query: async (query, context, matches) => {
    return authenticatedFetch(`${API_BASE_URL}/ai/search`, {
      method: 'POST',
      body: JSON.stringify({ 
        query,
        context,
        matches
      })
    });
  }
};

// ============================================
// PROJECTS API (PlantADay specific)
// ============================================
export const projectsAPI = {
  // Create new project with plan upload
  create: async (formData) => {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE_URL}/projects`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData // FormData with file upload
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }
    
    return response.json();
  },
  
  // Get all projects for a designer
  getAll: async (designerId) => {
    return authenticatedFetch(`${API_BASE_URL}/projects/designer/${designerId}`);
  },
  
  // Get single project details
  get: async (projectId) => {
    return authenticatedFetch(`${API_BASE_URL}/projects/${projectId}`);
  },
  
  // Update project
  update: async (projectId, updates) => {
    return authenticatedFetch(`${API_BASE_URL}/projects/${projectId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  },
  
  // Delete project
  delete: async (projectId) => {
    return authenticatedFetch(`${API_BASE_URL}/projects/${projectId}`, {
      method: 'DELETE'
    });
  },
  
  // Upload file to project
  uploadFile: async (projectId, formData) => {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE_URL}/projects/${projectId}/files`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }
    
    return response.json();
  },
  
  // Get all files for a project
  getFiles: async (projectId, shareToken = null) => {
    if (shareToken) {
      const url = `${API_BASE_URL}/projects/${projectId}/files?shareToken=${encodeURIComponent(shareToken)}`;
      const response = await fetch(url);
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(error.error || `HTTP ${response.status}`);
      }
      return response.json();
    }
    return authenticatedFetch(`${API_BASE_URL}/projects/${projectId}/files`);
  },
  
  // Delete a project file
  deleteFile: async (projectId, fileId) => {
    return authenticatedFetch(`${API_BASE_URL}/projects/${projectId}/files/${fileId}`, {
      method: 'DELETE'
    });
  }
};

// ============================================
// PROJECT PLANS API (Multiple drawings per project)
// ============================================

export const projectPlansAPI = {
  list: async (projectId) => {
    return authenticatedFetch(`${API_BASE_URL}/project-plans/project/${projectId}`);
  },

  create: async (projectId, formData) => {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE_URL}/project-plans/project/${projectId}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  },

  update: async (planId, updates) => {
    return authenticatedFetch(`${API_BASE_URL}/project-plans/${planId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  },

  delete: async (planId) => {
    return authenticatedFetch(`${API_BASE_URL}/project-plans/${planId}`, {
      method: 'DELETE'
    });
  },

  listNotes: async (planId) => {
    return authenticatedFetch(`${API_BASE_URL}/project-plans/${planId}/notes`);
  },

  createNote: async (planId, note) => {
    return authenticatedFetch(`${API_BASE_URL}/project-plans/${planId}/notes`, {
      method: 'POST',
      body: JSON.stringify(note)
    });
  },

  updateNote: async (noteId, updates) => {
    return authenticatedFetch(`${API_BASE_URL}/project-plans/notes/${noteId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  },

  deleteNote: async (noteId) => {
    return authenticatedFetch(`${API_BASE_URL}/project-plans/notes/${noteId}`, {
      method: 'DELETE'
    });
  },

  transferFrom: async (targetPlanId, { fromPlanId, copyNotes = false, copyPlants = false }) => {
    return authenticatedFetch(`${API_BASE_URL}/project-plans/${targetPlanId}/transfer-from`, {
      method: 'POST',
      body: JSON.stringify({ fromPlanId, copyNotes, copyPlants })
    });
  }
};

// ============================================
// PLANTS API
// ============================================
export const plantsAPI = {
  // Get all plants with optional search and filter
  getAll: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.category) queryParams.append('category', params.category);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    
    const response = await authenticatedFetch(`${API_BASE_URL}/plants?${queryParams.toString()}`);
    return response.plants || [];
  },
  
  // Get single plant details
  get: async (plantId) => {
    return authenticatedFetch(`${API_BASE_URL}/plants/${plantId}`);
  },
  
  // Create new plant
  create: async (plantData) => {
    return authenticatedFetch(`${API_BASE_URL}/plants`, {
      method: 'POST',
      body: JSON.stringify(plantData)
    });
  },
  
  // Update plant
  update: async (plantId, updates) => {
    return authenticatedFetch(`${API_BASE_URL}/plants/${plantId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  },
  
  // Delete plant
  delete: async (plantId) => {
    return authenticatedFetch(`${API_BASE_URL}/plants/${plantId}`, {
      method: 'DELETE'
    });
  },
  
  // Get categories
  getCategories: async () => {
    const response = await authenticatedFetch(`${API_BASE_URL}/plants/meta/categories`);
    return response.categories || [];
  }
};

// ============================================
// PLANT PLACEMENTS API
// ============================================
export const plantPlacementsAPI = {
  // Get all placements for a project
  getByProject: async (projectId, { planId } = {}) => {
    const qs = planId ? `?planId=${encodeURIComponent(planId)}` : '';
    const debugHeaders = (() => {
      try {
        return localStorage.getItem('debugPlacement') === '1' ? { 'X-Placement-Debug': '1' } : {};
      } catch {
        return {};
      }
    })();
    const response = await authenticatedFetch(`${API_BASE_URL}/plant-placements/project/${projectId}${qs}`, {
      headers: debugHeaders
    });
    return response.placements || [];
  },
  
  // Add plant to project
  create: async (placementData) => {
    const debugHeaders = (() => {
      try {
        return localStorage.getItem('debugPlacement') === '1' ? { 'X-Placement-Debug': '1' } : {};
      } catch {
        return {};
      }
    })();
    const response = await authenticatedFetch(`${API_BASE_URL}/plant-placements`, {
      method: 'POST',
      headers: debugHeaders,
      body: JSON.stringify(placementData)
    });
    return response.placement || response;
  },
  
  // Update placement
  update: async (placementId, updates) => {
    return authenticatedFetch(`${API_BASE_URL}/plant-placements/${placementId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  },
  
  // Delete placement
  delete: async (placementId) => {
    return authenticatedFetch(`${API_BASE_URL}/plant-placements/${placementId}`, {
      method: 'DELETE'
    });
  }
};

// ============================================
// SOCIAL MEDIA API
// ============================================
export const socialMediaAPI = {
  // Get connected social media accounts
  getAccounts: async () => {
    return authenticatedFetch(`${API_BASE_URL}/social-media/accounts`);
  },
  
  // Get Facebook OAuth URL
  getFacebookAuthUrl: async () => {
    return authenticatedFetch(`${API_BASE_URL}/social-media/facebook/auth-url`);
  },
  
  // Disconnect social media account
  disconnectAccount: async (platform) => {
    return authenticatedFetch(`${API_BASE_URL}/social-media/accounts/${platform}`, {
      method: 'DELETE'
    });
  },
  
  // Post to Facebook
  postToFacebook: async (message, imageUrl, plantId) => {
    return authenticatedFetch(`${API_BASE_URL}/social-media/facebook/post`, {
      method: 'POST',
      body: JSON.stringify({ message, imageUrl, plantId })
    });
  },
  
  // Post to Instagram
  postToInstagram: async (caption, imageUrl, plantId) => {
    return authenticatedFetch(`${API_BASE_URL}/social-media/instagram/post`, {
      method: 'POST',
      body: JSON.stringify({ caption, imageUrl, plantId })
    });
  }
};

// ============================================
// COLLABORATION API
// ============================================
export const collaborationAPI = {
  // Get all collaborators across all projects
  getAllCollaborators: async () => {
    return authenticatedFetch(`${API_BASE_URL}/collaboration/all-collaborators`);
  },
  // Update message visibility for a collaborator
  updateMessageVisibility: async (shareId, visibility) => {
    return authenticatedFetch(`${API_BASE_URL}/collaboration/share/${shareId}`, {
      method: 'PUT',
      body: JSON.stringify({ message_visibility: visibility })
    });
  },
  // Update schedule access for a collaborator
  updateScheduleAccess: async (shareId, access) => {
    return authenticatedFetch(`${API_BASE_URL}/collaboration/share/${shareId}`, {
      method: 'PUT',
      body: JSON.stringify({ schedule_access: access })
    });
  },
  // Delete/revoke a collaborator share
  deleteShare: async (shareId) => {
    return authenticatedFetch(`${API_BASE_URL}/collaboration/share/${shareId}`, {
      method: 'DELETE'
    });
  }
};

// ============================================
// DESIGNER ENDORSEMENTS API
// ============================================
export const endorsementsAPI = {
  // Get all endorsements for current designer
  getAll: async () => {
    return authenticatedFetch(`${API_BASE_URL}/designers/endorsements`);
  },
  // Create a new endorsement
  create: async (endorsementData) => {
    return authenticatedFetch(`${API_BASE_URL}/designers/endorsements`, {
      method: 'POST',
      body: JSON.stringify(endorsementData)
    });
  },
  // Update endorsement status (toggle visibility)
  update: async (endorsementId, status) => {
    return authenticatedFetch(`${API_BASE_URL}/designers/endorsements/${endorsementId}`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    });
  },
  // Remove an endorsement
  remove: async (endorsementId) => {
    return authenticatedFetch(`${API_BASE_URL}/designers/endorsements/${endorsementId}`, {
      method: 'DELETE'
    });
  },
  // Get designers who endorsed a specific designer
  getEndorsedBy: async (designerId) => {
    return fetch(`${API_BASE_URL}/designers/${designerId}/endorsed-by`).then(response => {
      if (!response.ok) {
        return response.json().then(error => { throw new Error(error.error || 'Failed to fetch endorsements'); });
      }
      return response.json();
    });
  }
};

// ============================================
// YARD ANALYSIS API
// ============================================
export const yardAnalysisAPI = {
  // Get consultation requests for a designer
  getRequests: async (designerId, status = null) => {
    const url = status 
      ? `${API_BASE_URL}/yard/requests/${designerId}?status=${status}`
      : `${API_BASE_URL}/yard/requests/${designerId}`;
    return authenticatedFetch(url);
  },
  
  // Update request status
  updateStatus: async (requestId, status) => {
    return authenticatedFetch(`${API_BASE_URL}/yard/requests/${requestId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    });
  }
};

export default {
  profileAPI,
  clientsAPI,
  visitsAPI,
  connectionsAPI,
  teamConnectionsAPI,
  teamVisitsAPI,
  designersAPI,
  messagesAPI,
  inviteLinksAPI,
  designerSearchAPI,
  perksAPI,
  matchWantedAPI,
  messagingAPI,
  aiSearchAPI,
  projectsAPI,
  projectPlansAPI,
  plantsAPI,
  plantPlacementsAPI,
  socialMediaAPI,
  collaborationAPI,
  yardAnalysisAPI,
  loadUserData,
  migrateLocalStorageData,
  // Convenience methods
  getDesignerProjects: async (designerId) => projectsAPI.getAll(designerId),
  createProject: async (formData) => projectsAPI.create(formData),
  getProject: async (projectId) => projectsAPI.get(projectId)
};

